import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2, Lock, FileText, ClipboardList, Mail, MailCheck, MailWarning, History, Eye } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { bookingsApi } from '../../services/api';
import { attachmentFilename, formatDate } from '../../lib/utils';
import { TelexReleaseCard } from './TelexReleaseCard';
import { IssuedVersions } from './IssuedVersions';
import { FreeTimeCard } from './FreeTimeCard';
import { DocumentPreview } from './DocumentPreview';
import { toast } from 'sonner';

function saveBlob(res, fallbackName) {
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url; link.setAttribute('download', attachmentFilename(res, fallbackName));
  document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
}

function EmailTrail({ email }) {
  if (!email) return null;
  const sent = email.status === 'sent';
  const Icon = sent ? MailCheck : MailWarning;
  return (
    <p className={"text-xs mt-0.5 inline-flex items-center gap-1 " + (sent ? 'text-success-700' : 'text-amber-800')} data-testid="doc-email-trail">
      <Icon className="h-3.5 w-3.5" />
      {sent ? 'Emailed ' + formatDate(email.at) + ' to ' + [...(email.to || []), ...(email.cc || [])].join(', ') : (email.to && email.to.length ? 'Not emailed — ' + (email.reason || 'delivery unavailable') : 'No recipient email on file')}
    </p>
  );
}

// Carrier-issued document set for one booking (B/L, waybill, manifest, invoice, notices, VGM, telex release).
export function CarrierDocuments({ bookings, basePath, initialBookingId, controlledBookingId, hideSelector = false }) {
  const [bookingId, setBookingId] = useState(controlledBookingId || initialBookingId || '');
  useEffect(function () { if (controlledBookingId !== undefined) setBookingId(controlledBookingId || ''); }, [controlledBookingId]);
  const [state, setState] = useState(null);
  const [booking, setBooking] = useState(null);
  const [archive, setArchive] = useState([]);
  const [openVersions, setOpenVersions] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const staff = basePath !== '/dashboard';
  const eligible = bookings.filter(function (b) { return b.status !== 'cancelled'; });

  const load = useCallback(async function () {
    if (!bookingId) { setState(null); setBooking(null); setArchive([]); return; }
    const res = await Promise.all([bookingsApi.carrierDocuments(bookingId), bookingsApi.getById(bookingId), bookingsApi.documentArchive(bookingId)]);
    setState(res[0].data); setBooking(res[1].data); setArchive(res[2].data.versions || []);
  }, [bookingId]);

  useEffect(function () {
    setLoading(true);
    load().catch(function () { toast.error('Could not load documents'); }).finally(function () { setLoading(false); });
  }, [load]);

  async function download(doc) {
    setBusy('dl-' + doc.type);
    try {
      const res = await bookingsApi.downloadCarrierDocument(bookingId, doc.type);
      saveBlob(res, doc.title.replace(/[^A-Za-z]/g, '') + '.pdf');
      toast.success(doc.title + ' downloaded');
      await load();
    } catch (err) {
      let msg = 'Could not generate the document';
      try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch (e) { /* keep default */ }
      toast.error(msg);
    } finally {
      setBusy('');
    }
  }

  const loadPreview = useCallback(async function () {
    if (!preview) return null;
    try {
      const res = await bookingsApi.previewCarrierDocument(bookingId, preview.type);
      return res.data;
    } catch (err) {
      throw err.response?.data?.detail || 'Could not render the document';
    }
  }, [bookingId, preview]);

  async function downloadArchived(v) {
    setBusy('arch-' + v.id);
    try {
      const res = await bookingsApi.downloadArchivedDocument(bookingId, v.id);
      saveBlob(res, v.filename);
      toast.success(v.title + ' v' + v.version + ' — issued copy downloaded');
    } catch (err) {
      toast.error('The archived copy could not be retrieved');
    } finally {
      setBusy('');
    }
  }

  async function send(doc) {
    setBusy('send-' + doc.type);
    try {
      const res = await bookingsApi.sendCarrierDocument(bookingId, doc.type);
      const r = res.data;
      if (r.status === 'sent') toast.success(doc.title + ' emailed to ' + [...r.to, ...(r.cc || [])].join(', '));
      else toast.warning(r.reason || 'Email not sent', { description: r.to && r.to.length ? 'Recipients: ' + r.to.join(', ') : 'Add contact emails to the shipping instructions' });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the document');
    } finally {
      setBusy('');
    }
  }

  const selected = eligible.find(function (b) { return b.id === bookingId; });
  return (
    <div className={hideSelector ? '' : 'bg-white rounded-sm shadow-card p-6'} data-testid="carrier-documents">
      {!hideSelector && (
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
          <div>
            <h2 className="text-lg font-medium text-ink flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> Carrier documents</h2>
            <p className="text-sm text-gray-600 mt-1">Bills of lading, sea waybill, manifest, invoice, VGM certificate, arrival and delivery notices — issued by Südvik Line as the shipment progresses and emailed to the parties on the shipping instructions.</p>
          </div>
          <Select value={bookingId} onValueChange={setBookingId}>
            <SelectTrigger className="h-11 rounded-sm md:w-96" data-testid="carrier-docs-booking-select"><SelectValue placeholder={eligible.length ? 'Select a booking (' + eligible.length + ')' : 'No bookings yet'} /></SelectTrigger>
            <SelectContent>
              {eligible.map(function (b) { return <SelectItem key={b.id} value={b.id}>{b.booking_number} — {b.origin_port.split(',')[0]} to {b.destination_port.split(',')[0]}</SelectItem>; })}
            </SelectContent>
          </Select>
        </div>
      )}

      {!bookingId && <p className="text-sm text-gray-500 py-6 text-center" data-testid="carrier-docs-empty">Choose a booking to see which documents are ready.</p>}
      {loading && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-500" /></div>}

      {state && booking && !loading && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm mb-4 p-3 bg-gray-50 rounded-sm">
            <span className="sv-mono">{state.booking_number}</span>
            {state.bl_number && <span className="text-gray-700">B/L <span className="sv-mono text-ink" data-testid="carrier-docs-bl">{state.bl_number}</span></span>}
            {selected && <span className="text-gray-600">{selected.status.replace(/_/g, ' ')}</span>}
            <span className={"inline-flex items-center gap-1.5 " + (state.shipping_instructions_submitted ? 'text-success-700' : 'text-amber-800')} data-testid="carrier-docs-si-state">
              <ClipboardList className="h-4 w-4" /> Shipping instructions {state.shipping_instructions_submitted ? 'submitted' : 'not yet submitted'}
            </span>
            <Link to={basePath + '/bookings/' + bookingId + '/shipping-instructions'} className="sv-link ml-auto" data-testid="carrier-docs-si-link">{state.shipping_instructions_submitted ? 'Shipping instructions & VGM' : 'Complete shipping instructions'}</Link>
          </div>

          <TelexReleaseCard booking={booking} staff={staff} onChanged={load} />
          <FreeTimeCard bookingId={bookingId} className="mb-4 border border-gray-100 shadow-none" testId="docs-free-time" />

          <ul className="divide-y divide-gray-200" data-testid="carrier-docs-list">
            {state.documents.map(function (doc) {
              const versions = archive.filter(function (v) { return v.type === doc.type; });
              const open = Boolean(openVersions[doc.type]);
              return (
                <li key={doc.type} className="py-3" data-testid={'carrier-doc-' + doc.type}>
                  <div className="flex items-center gap-4">
                    <div className={"w-9 h-9 rounded-sm flex items-center justify-center shrink-0 " + (doc.available ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400')}>{doc.available ? <FileText className="h-4 w-4" /> : <Lock className="h-4 w-4" />}</div>
                    <div className="flex-1 min-w-0">
                      <p className={"font-medium " + (doc.available ? 'text-ink' : 'text-gray-500')}>{doc.title}{doc.number && <span className="sv-mono text-xs text-gray-500 ml-2">{doc.number}</span>}{doc.latest_version && <span className="sv-mono text-[11px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded ml-2" title={'Latest issued ' + formatDate(doc.latest_version.issued_at)} data-testid={'carrier-doc-version-' + doc.type}>v{doc.latest_version.version}</span>}</p>
                      <p className="text-sm text-gray-600 truncate">{doc.available ? doc.description : doc.reason}</p>
                      {doc.available && doc.emailable && <EmailTrail email={doc.email} />}
                      {versions.length > 0 && (
                        <button type="button" onClick={function () { setOpenVersions(function (p) { return { ...p, [doc.type]: !open }; }); }} className="text-xs text-blue-700 hover:underline flex items-center gap-1 mt-1" data-testid={'carrier-doc-versions-toggle-' + doc.type}>
                          <History className="h-3.5 w-3.5" /> {open ? 'Hide' : 'Show'} issued versions ({versions.length})
                        </button>
                      )}
                    </div>
                    {doc.available && doc.emailable && (
                      <Button size="sm" variant="outline" disabled={busy !== ''} className="rounded-sm" title="Send by email to the parties on the shipping instructions" onClick={function () { send(doc); }} data-testid={'carrier-doc-send-' + doc.type}>
                        {busy === 'send-' + doc.type ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" /> {doc.email ? 'Resend' : 'Email'}</>}
                      </Button>
                    )}
                    {doc.available && (
                      <Button size="sm" variant="outline" disabled={busy !== ''} className="rounded-sm" title="Preview pages before downloading" onClick={function () { setPreview(doc); }} data-testid={'carrier-doc-preview-' + doc.type}>
                        <Eye className="h-4 w-4 mr-1.5" /> Preview
                      </Button>
                    )}
                    <Button size="sm" variant={doc.available ? 'default' : 'outline'} disabled={!doc.available || busy !== ''} className={"rounded-sm " + (doc.available ? 'bg-ink hover:bg-ink-800' : '')} onClick={function () { download(doc); }} data-testid={'carrier-doc-download-' + doc.type}>
                      {busy === 'dl-' + doc.type ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> PDF</>}
                    </Button>
                  </div>
                  {open && <IssuedVersions versions={versions} docType={doc.type} busyId={busy.startsWith('arch-') ? busy.slice(5) : ''} onDownload={downloadArchived} />}
                </li>
              );
            })}
          </ul>
        </>
      )}
      <DocumentPreview open={Boolean(preview)} onOpenChange={function (o) { if (!o) setPreview(null); }} load={loadPreview} title={preview ? preview.title : ''} downloading={preview ? busy === 'dl-' + preview.type : false}
        onDownload={preview ? async function () { await download(preview); setPreview(null); } : null} />
    </div>
  );
}
